import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ConfigurationGroupType, TimeSlotType } from 'shared-types/SpaPackage';

import { SpaPackageFormValues } from '../../SpaPackageForm.schema';
import { shouldRenderConfigGroup } from '../SpaDayBooking.helpers';
import { countStartTimesHeldByOtherGuests, isStartTimeAtCapacity } from '../timeslotCapacity';
import {
  getGuestBookedStartTimes,
  getTreatmentOptionRefs,
  isStartTimeTakenByOtherOption,
  isTreatmentOption,
  OptionRef,
} from '../timeslotClash';

type Guest = SpaPackageFormValues['guests'][number];
type GuestConfigGroups = NonNullable<Guest['configurationGroups']>;

type UseSelectSoleTimeslotParams = {
  form: UseFormReturn<SpaPackageFormValues>;
  configGroupsNoExtras: ConfigurationGroupType[];
  watchedGuests: SpaPackageFormValues['guests'] | undefined;
  requireTimeslots: boolean;
};

function offeredTimeslots(option: ConfigurationGroupType['options'][number]): TimeSlotType[] {
  return Array.isArray(option.timeslots) ? option.timeslots : [];
}

function findSoleTimeslot(
  option: ConfigurationGroupType['options'][number],
  groupId: number,
  bookedStartTimes: Map<string, OptionRef[]>,
  startTimesHeldByOtherGuests: Map<string, number>
): TimeSlotType | undefined {
  const ref: OptionRef = { configurationGroupId: groupId, optionId: option.id };

  const selectable = offeredTimeslots(option).filter(
    (timeslot) =>
      !isStartTimeAtCapacity(startTimesHeldByOtherGuests, ref, timeslot) &&
      (!isTreatmentOption(option) ||
        !isStartTimeTakenByOtherOption(bookedStartTimes, timeslot.startTime, ref))
  );

  return selectable.length === 1 ? selectable[0] : undefined;
}

function dropTimeslotsNoLongerOffered(
  configGroups: GuestConfigGroups,
  groups: ConfigurationGroupType[]
): boolean {
  let dropped = false;

  groups.forEach((group) => {
    const groupKey = `group_${group.id}`;
    const selections = configGroups[groupKey];
    if (!selections) return;

    let nextSelections = selections;

    group.options.forEach((option, idx) => {
      const selection = nextSelections[idx];
      const held = selection?.timeslot;
      if (!held) return;
      if (offeredTimeslots(option).some((timeslot) => timeslot.startTime === held.startTime)) {
        return;
      }

      if (nextSelections === selections) nextSelections = [...selections];
      nextSelections[idx] = { ...selection, timeslot: null };
    });

    if (nextSelections === selections) return;
    configGroups[groupKey] = nextSelections;
    dropped = true;
  });

  return dropped;
}

export function useSelectSoleTimeslot({
  form,
  configGroupsNoExtras,
  watchedGuests,
  requireTimeslots,
}: UseSelectSoleTimeslotParams) {
  useEffect(() => {
    if (!requireTimeslots) return;

    const groups = configGroupsNoExtras.filter(shouldRenderConfigGroup);
    if (groups.length === 0) return;

    const currentGuests = form.getValues('guests') || [];
    if (currentGuests.length === 0) return;

    const treatmentOptions = getTreatmentOptionRefs(configGroupsNoExtras);
    let changed = false;

    const nextGuests = [...currentGuests];

    nextGuests.forEach((guest, guestIndex) => {
      const configGroups: GuestConfigGroups = { ...(guest.configurationGroups || {}) };
      let guestChanged = dropTimeslotsNoLongerOffered(configGroups, groups);

      const bookedStartTimes = getGuestBookedStartTimes(configGroups, treatmentOptions);
      const startTimesHeldByOtherGuests = countStartTimesHeldByOtherGuests(nextGuests, guestIndex);

      groups.forEach((group) => {
        const groupKey = `group_${group.id}`;
        const selections = configGroups[groupKey];
        if (!selections) return;

        let nextSelections = selections;

        group.options.forEach((option, idx) => {
          const selection = nextSelections[idx];
          if (!selection?.isSelected || selection.timeslot) return;

          const timeslot = findSoleTimeslot(
            option,
            group.id,
            bookedStartTimes,
            startTimesHeldByOtherGuests
          );
          if (!timeslot) return;

          if (nextSelections === selections) nextSelections = [...selections];
          nextSelections[idx] = {
            ...selection,
            timeslot: { startTime: timeslot.startTime, endTime: timeslot.endTime },
          };

          if (isTreatmentOption(option)) {
            const holders = bookedStartTimes.get(timeslot.startTime) ?? [];
            holders.push({ configurationGroupId: group.id, optionId: option.id });
            bookedStartTimes.set(timeslot.startTime, holders);
          }
        });

        if (nextSelections === selections) return;
        configGroups[groupKey] = nextSelections;
        guestChanged = true;
      });

      if (!guestChanged) return;
      changed = true;
      nextGuests[guestIndex] = { ...guest, configurationGroups: configGroups };
    });

    if (changed) {
      form.setValue('guests', nextGuests as SpaPackageFormValues['guests']);
    }
  }, [configGroupsNoExtras, watchedGuests, form, requireTimeslots]);
}
